// GET /threads — list past conversations for the sidebar.
// GET /threads/{id} — replay a thread.

import type { ChartSpec } from "../features/charts/types";
import { api } from "./base";

export interface ThreadSummary {
  id: number;
  title: string | null;
  organization_name: string;
  created_at: string;
  message_count: number;
}

export interface AssistantResponse {
  final?: {
    text: string;
    insights: string[];
    follow_ups: string[];
    chart_ids: string[];
  };
  charts?: ChartSpec[];
  tool_summaries?: { name: string; ok: boolean; input?: Record<string, unknown> }[];
}

export interface ThreadMessage {
  id: number;
  user_query: string;
  assistant_response: AssistantResponse;
  created_at: string;
}

export interface ThreadDetail {
  id: number;
  title: string | null;
  organization_name: string;
  created_at: string;
  messages: ThreadMessage[];
}

export async function fetchThreads(bearer: string): Promise<ThreadSummary[]> {
  const resp = await fetch(api("/threads"), {
    headers: { Authorization: `Bearer ${bearer}` },
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`/threads returned ${resp.status}: ${text}`);
  }
  return (await resp.json()) as ThreadSummary[];
}

export async function fetchThread(bearer: string, threadId: number): Promise<ThreadDetail> {
  const resp = await fetch(`${api("/threads")}/${threadId}`, {
    headers: { Authorization: `Bearer ${bearer}` },
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`${api("/threads")}/${threadId} returned ${resp.status}: ${text}`);
  }
  return (await resp.json()) as ThreadDetail;
}

export async function deleteThread(bearer: string, threadId: number): Promise<void> {
  const resp = await fetch(`${api("/threads")}/${threadId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`DELETE /threads/${threadId} returned ${resp.status}: ${text}`);
  }
}

export async function renameThread(bearer: string, threadId: number, title: string): Promise<string> {
  const resp = await fetch(`${api("/threads")}/${threadId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify({ title }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`PATCH /threads/${threadId} returned ${resp.status}: ${text}`);
  }
  const data = (await resp.json()) as { title: string };
  return data.title;
}
